import { Autocomplete, TextField } from "@mui/material";
import { useEffect, useState } from "react";

import { inject } from "../../../../../../../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../../../../../../../hooks/useService";

import ShowMoreButtonAutoComp from "./ShowMoreButtonAutoComp";

export default function AutoCompleteSearch({
  activeEditObj,
  apiUrl,
  customLimit,
  displayKey,
  error,
  multiple = false,
  nullable,
  required,
  setVal,
  title,
}) {
  const currentUserService = inject("current-user-service");
  const { userRoles } = currentUserService;
  const enumRepository = useService("repositories/enum-repository");
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [resLimit, setResLimit] = useState(customLimit ? customLimit : 25);
  const [showMoreVisible, setShowMoreVisible] = useState(false);

  const noSelectionLabel = "No Selection";
  const nullOption = { __isNull: true, [displayKey]: noSelectionLabel };
  const displayOptions = nullable ? [nullOption, ...(options || [])] : options || [];

  // helper to normalize id/key from option or primitive
  const getIdFromOption = (opt) => {
    if (opt === null) {
      return null;
    }
    if (typeof opt === "object") {
      return opt?.key ?? opt?.pk ?? opt?.id;
    }
    return opt; // primitive id
  };

  async function fetchOpts() {
    try {
      const urlPath = apiUrl + `&limit=${resLimit}` + `${search ? `&search=${search}` : ""}`;
      const resp = await enumRepository.query({ urlPath });
      const count = await enumRepository.getTotalCount();
      setOptions(resp || []);
      if (count <= resLimit) {
        setShowMoreVisible(false);
      } else {
        setShowMoreVisible(true);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (userRoles.permissions.includes("utilities.view_fieldoptions")) {
      fetchOpts();
    }
  }, [userRoles.permissions, setOptions, search, resLimit]);

  // controlled selected value(s) to handle async options and primitives
  const [selected, setSelected] = useState(() => {
    if (multiple) {
      if (Array.isArray(activeEditObj)) {
        return activeEditObj;
      }
      return [];
    } else {
      return activeEditObj ?? (nullable ? nullOption : null);
    }
  });

  // sync selected when activeEditObj or options load/change
  useEffect(() => {
    if (multiple) {
      if (!activeEditObj) {
        setSelected([]);
        return;
      }
      if (Array.isArray(activeEditObj)) {
        // if array of primitives, map to option objects when possible
        if (activeEditObj.length === 0) {
          setSelected([]);
          return;
        }
        const primitives = activeEditObj.every((a) => typeof a !== "object");
        if (primitives) {
          const mapped = activeEditObj
            .map((id) => displayOptions.find((o) => String(getIdFromOption(o)) === String(id)))
            .filter(Boolean);
          setSelected(mapped);
        } else {
          setSelected(activeEditObj);
        }
      } else {
        setSelected([]);
      }
    } else {
      if (activeEditObj === null) {
        setSelected(nullable ? nullOption : null);
        return;
      }
      if (typeof activeEditObj === "object") {
        setSelected(activeEditObj);
      } else {
        const found = displayOptions.find(
          (o) => String(getIdFromOption(o)) === String(activeEditObj)
        );
        setSelected(found ?? (nullable ? nullOption : null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEditObj, options, nullable, multiple]);

  return (
    <Autocomplete
      className="w-full"
      disablePortal
      multiple={multiple}
      freeSolo={!multiple}
      size="small"
      clearOnBlur={true}
      options={!displayOptions ? [] : displayOptions}
      value={selected}
      getOptionLabel={(option) => {
        if (!option) {
          return "";
        }
        if (option.__isNull) {
          return option[displayKey] || noSelectionLabel;
        }
        return !option[displayKey] ? "" : option[displayKey];
      }}
      noOptionsText="No Results"
      onChange={(e, value) => {
        // value is either a single option (when !multiple) or an array (when multiple)
        if (multiple) {
          // if includes nullOption, treat as empty selection
          if (value?.some((v) => v && v?.__isNull)) {
            setSelected([]);
            setVal([]); // send empty array to parent
            setSearch("");
            return;
          }
          setSelected(value);
          const ids = value.map((v) => getIdFromOption(v));
          setVal(ids);
        } else {
          // IMPORTANT: Autocomplete expects the `value` to be an option object (or null),
          // not a primitive id. Keep `selected` as the object so the component can render the label.
          if (value && value?.__isNull) {
            setSelected(null);
            setVal(null);
          } else {
            setSelected(value);
            setVal(getIdFromOption(value));
          }
        }
        setSearch("");
      }}
      renderOption={(props, option) => {
        const key =
          option && option.__isNull
            ? "no-selection"
            : `${option?.[displayKey] ?? "opt"}-${option?.id ?? option?.pk ?? Math.random()}`;
        return (
          <li className="text-left" {...props} id={option[displayKey]} key={key}>
            {option[displayKey]}
          </li>
        );
      }}
      renderInput={(params) => (
        <div className="flex items-center">
          <TextField
            {...params}
            label={title}
            onChange={(e) => setSearch(e.target.value)}
            required={!required ? false : true}
            error={error}
          />
          <ShowMoreButtonAutoComp
            showMoreVisible={showMoreVisible}
            setResLimit={setResLimit}
            resLimit={resLimit}
            showMoreAmt={10}
            customTitle={title}
          />
        </div>
      )}
    />
  );
}
